import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Dayjs } from 'dayjs';
import { getConfig } from '@app/services/configService';

interface LicensesFilterState {
  searchText: string;
  selectedLicenseTypeIds: number[];
  createdAtFromFilter: Dayjs | null | undefined;
  createdAtToFilter: Dayjs | null | undefined;
  fromFilter: Dayjs | null | undefined;
  toFilter: Dayjs | null | undefined;
  selectedLicenseTypeIdsTemporal: number[];
  showNotPrinted: boolean;
  notFormalVerified: boolean;
  selectedStatuses: number[];
  selectedStatusesTemporal: number[];
  selectedSalesPointIds: string[];
  selectedSalesPointIdsTemp: string[];
  mobileUsed: boolean | null;
  mobileUsedTemp: boolean | null;
}

const initialState: LicensesFilterState = {
  searchText: '',
  selectedLicenseTypeIds: [],
  createdAtFromFilter: undefined,
  createdAtToFilter: undefined,
  fromFilter: undefined,
  toFilter: undefined,
  selectedLicenseTypeIdsTemporal: [],
  showNotPrinted: false,
  notFormalVerified: false,
  selectedStatuses: [] as number[],
  selectedStatusesTemporal: [] as number[],
  selectedSalesPointIds: [],
  selectedSalesPointIdsTemp: [],
  mobileUsed: null,
  mobileUsedTemp: null,
};

const licensesFiltersSlice = createSlice({
  name: 'licensesFilters',
  initialState,
  reducers: {
    setSearchText(state, action: PayloadAction<string>) {
      state.searchText = action.payload;
    },
    setSelectedLicenseTypeIds(state, action: PayloadAction<number[]>) {
      state.selectedLicenseTypeIds = action.payload;
    },
    setCreatedAtFromFilter(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.createdAtFromFilter = action.payload;
    },
    setCreatedAtToFilter(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.createdAtToFilter = action.payload;
    },
    setFromFilter(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.fromFilter = action.payload;
    },
    setToFilter(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.toFilter = action.payload;
    },
    setSelectedLicenseTypeIdsTemporal(state, action: PayloadAction<number[]>) {
      state.selectedLicenseTypeIdsTemporal = action.payload;
    },
    setShowNotPrinted: (state, action: PayloadAction<boolean>) => {
      state.showNotPrinted = action.payload;
    },
    setNotFormalVerified: (state, action: PayloadAction<boolean>) => {
      state.notFormalVerified = action.payload;
    },
    setSelectedStatuses: (state, action: PayloadAction<number[]>) => {
      state.selectedStatuses = action.payload;
    },
    setSelectedStatusesTemporal: (state, action: PayloadAction<number[]>) => {
      state.selectedStatusesTemporal = action.payload;
    },
    setSelectedSalesPointIds: (state, action: PayloadAction<string[]>) => {
      state.selectedSalesPointIds = action.payload;
    },
    setSelectedSalesPointIdsTemp: (state, action: PayloadAction<string[]>) => {
      state.selectedSalesPointIdsTemp = action.payload;
    },
    setMobileUsed: (state, action: PayloadAction<boolean | null>) => {
      state.mobileUsed = action.payload;
    },
    setMobileUsedTemp: (state, action: PayloadAction<boolean | null>) => {
      state.mobileUsedTemp = action.payload;
    },
    resetFilters(state) {
      state.searchText = initialState.searchText;
      state.selectedLicenseTypeIds = initialState.selectedLicenseTypeIds;
      state.createdAtFromFilter = initialState.createdAtFromFilter;
      state.createdAtToFilter = initialState.createdAtToFilter;
      state.fromFilter = initialState.fromFilter;
      state.toFilter = initialState.toFilter;
      state.selectedLicenseTypeIdsTemporal = initialState.selectedLicenseTypeIdsTemporal;
      state.showNotPrinted = initialState.showNotPrinted;
      state.notFormalVerified = initialState.notFormalVerified;
      state.selectedStatuses = initialState.selectedStatuses;
      state.selectedStatusesTemporal = initialState.selectedStatusesTemporal;
      state.selectedSalesPointIds = initialState.selectedSalesPointIds;
      state.selectedSalesPointIdsTemp = initialState.selectedSalesPointIdsTemp;
      state.mobileUsed = initialState.mobileUsed;
      state.mobileUsedTemp = initialState.mobileUsedTemp;
    },
  },
});

export const {
  setSearchText,
  setSelectedLicenseTypeIds,
  setCreatedAtFromFilter,
  setCreatedAtToFilter,
  setFromFilter,
  setToFilter,
  setSelectedLicenseTypeIdsTemporal,
  setShowNotPrinted,
  setNotFormalVerified,
  setSelectedStatuses,
  setSelectedStatusesTemporal,
  setSelectedSalesPointIds,
  setSelectedSalesPointIdsTemp,
  setMobileUsed,
  setMobileUsedTemp,
  resetFilters,
} = licensesFiltersSlice.actions;

export const areFiltersAppliedSelector = (state: { licensesFilters: LicensesFilterState }) => {
  const { searchText, selectedLicenseTypeIds, createdAtFromFilter, createdAtToFilter, fromFilter, toFilter } =
    state.licensesFilters;

  return (
    searchText.length > 0 ||
    selectedLicenseTypeIds.length > 0 ||
    createdAtFromFilter ||
    createdAtToFilter ||
    fromFilter ||
    toFilter
  );
};

export const activeFiltersCountSelector = (state: { licensesFilters: LicensesFilterState }) => {
  const {
    searchText,
    selectedLicenseTypeIds,
    createdAtFromFilter,
    createdAtToFilter,
    fromFilter,
    toFilter,
    showNotPrinted,
    notFormalVerified,
    selectedStatuses,
    selectedSalesPointIds,
    mobileUsed,
  } = state.licensesFilters;

  let count = 0;
  if (searchText.length > 0) count++;
  if (selectedLicenseTypeIds.length > 0) count++;
  if (createdAtFromFilter) count++;
  if (createdAtToFilter) count++;
  if (fromFilter) count++;
  if (toFilter) count++;
  const { licensesNotPrintedFilterHidden, licensesNotFormalVerifiedFilterHidden } = getConfig();
  if (showNotPrinted && !licensesNotPrintedFilterHidden) count++;
  if (notFormalVerified && !licensesNotFormalVerifiedFilterHidden) count++;
  if (selectedStatuses.length > 0) count++;
  if (selectedSalesPointIds.length > 0) count++;
  if (mobileUsed !== null) count++;

  return count;
};

export const licensesFiltersActions = licensesFiltersSlice.actions;

export const licensesFilters = licensesFiltersSlice.reducer;

export const showNotPrintedSelector = (state: { licensesFilters: LicensesFilterState }) =>
  state.licensesFilters.showNotPrinted;

export const selectedStatusesSelector = (state: { licensesFilters: LicensesFilterState }) =>
  state.licensesFilters.selectedStatuses;

export const selectedStatusesTemporalSelector = (state: { licensesFilters: LicensesFilterState }) =>
  state.licensesFilters.selectedStatusesTemporal;

export const selectedSalesPointIdsSelector = (state: { licensesFilters: LicensesFilterState }) =>
  state.licensesFilters.selectedSalesPointIds;

export const selectedSalesPointIdsTempSelector = (state: { licensesFilters: LicensesFilterState }) =>
  state.licensesFilters.selectedSalesPointIdsTemp;

export const mobileUsedSelector = (state: { licensesFilters: LicensesFilterState }) => state.licensesFilters.mobileUsed;

export const mobileUsedTempSelector = (state: { licensesFilters: LicensesFilterState }) =>
  state.licensesFilters.mobileUsedTemp;
